/**
 * Lightweight client-side error reporting. Frontend crashes were previously
 * invisible; this forwards them to the backend (`POST /client-errors`) so they
 * show up in server logs / alerting. Fire-and-forget, never throws, and
 * de-duplicates a burst of identical messages so a render-loop can't spam.
 *
 * Intentionally dependency-free (no Sentry) — a DSN-based tool can be layered
 * on later by swapping the body of reportClientError.
 */
const API_URL = import.meta.env.VITE_API_URL ?? "/api/v1";
const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? undefined;

type Source = "error_boundary" | "window_error" | "unhandled_rejection";

const recent = new Map<string, number>();
const DEDUP_MS = 10_000;

export function reportClientError(input: {
	message: string;
	stack?: string;
	source: Source;
}): void {
	try {
		const key = `${input.source}:${input.message}`.slice(0, 200);
		const now = Date.now();
		const last = recent.get(key);
		if (last && now - last < DEDUP_MS) return;
		recent.set(key, now);
		if (recent.size > 50) recent.clear();

		const body = JSON.stringify({
			message: input.message.slice(0, 2000),
			stack: input.stack?.slice(0, 8000),
			source: input.source,
			url:
				typeof location !== "undefined"
					? location.href.slice(0, 2000)
					: undefined,
			user_agent:
				typeof navigator !== "undefined"
					? navigator.userAgent.slice(0, 500)
					: undefined,
			app_version: APP_VERSION,
		});

		// keepalive so reports still flush during a navigation/unload.
		void fetch(`${API_URL}/client-errors`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body,
			keepalive: true,
		}).catch(() => undefined);
	} catch {
		// never let reporting throw
	}
}

let installed = false;

/** Wire global window error + unhandled-rejection handlers once. */
export function installGlobalErrorHandlers(): void {
	if (installed || typeof window === "undefined") return;
	installed = true;

	window.addEventListener("error", (e) => {
		reportClientError({
			message: e.message || "window error",
			stack: e.error?.stack,
			source: "window_error",
		});
	});

	window.addEventListener("unhandledrejection", (e) => {
		const reason = e.reason;
		reportClientError({
			message: reason?.message ?? String(reason ?? "unhandled rejection"),
			stack: reason?.stack,
			source: "unhandled_rejection",
		});
	});
}
